import React, { useState, useEffect, useRef } from "react";
import Button from "../../customComponents/Button";
import Table from "../../customComponents/Table";
import { createColumnHelper } from "@tanstack/react-table";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import DatePicker from "../../customComponents/DatePicker";
import log from "../../assets/img/Itinerary/log.png";
import { post } from "../../utils/api";
import { generateItineraryPDF } from "../pdfGenerator";
import SearchSelect from "../../customComponents/SerachSelect";
import LoaderComponent from "../LoaderComponent";
import { slugify } from "../../utils/slugify";

const AllItinerary = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const searchText = searchParams.get("search") || "";
  const packageFilter = searchParams.get("packagename") || "";
  const schoolFilter = searchParams.get("schoolname") || "";

  const updateParams = (updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => {
        if (v) next.set(k, v);
        else next.delete(k);
      });
      return next;
    });
  };

  const [localSearch, setLocalSearch] = useState(searchText);
  const searchTimerRef = useRef(null);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [packageOptions, setPackageOptions] = useState([]);
  const [institutionOptions, setInstitutionOptions] = useState([]);
  const [noOfPages, setNoOfPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [currentPage, searchText, packageFilter, schoolFilter]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const data = {
        page: currentPage,
        limit: 10,
        search: searchText.trim(),
        packagename: packageFilter,
        schoolname: schoolFilter,
      };
      const result = await post("/getAllItinerary", data);
      setNoOfPages(result.pagination.total);

      const temp = result.data.map((list, i) => ({
        id: i + 1,
        itineraryName: `${list.itineraryname} ${list.isdraft ? "Draft" : ""}`,
        package: list.packagename,
        dateoftravel: new Date(list.itinerary_travel).toISOString().split("T")[0],
        manager: "-",
        ...list,
      }));
      setTableData(temp);

      if (!optionsLoaded) {
        const packageSet = new Set(result.data.map((item) => item.packagename));
        const institutionSet = new Set(result.data.map((item) => item.schoolname));
        setPackageOptions([
          { label: "All Package", value: "All Package" },
          ...Array.from(packageSet).filter(Boolean).map((p) => ({ label: p, value: p })),
        ]);
        setInstitutionOptions([
          { label: "All Institution", value: "All Institution" },
          ...Array.from(institutionSet).filter(Boolean).map((s) => ({ label: s, value: s })),
        ]);
        setOptionsLoaded(true);
      }

      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching itineraries:", error);
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setLocalSearch(value);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      updateParams({ search: value || null, page: null });
    }, 400);
  };

  const handleFormSelection = (e, type) => {
    if (type === "package") {
      updateParams({ packagename: e.value !== "All Package" ? e.value : null, page: null });
    } else {
      updateParams({ schoolname: e.value !== "All Institution" ? e.value : null, page: null });
    }
  };

  const handleDelete = async () => {
    try {
      await post("/deleteItinerary", selectedPackage);
      setShowDeleteModal(false);
      fetchData();
      toast.success("Itinerary deleted successfully");
    } catch (error) {
      console.error("Error deleting itinerary:", error);
      toast.error("Failed to delete itinerary");
    }
  };

  const handleEditClick = (itineraryData) => {
    navigate("/add-itinerary", {
      state: { itineraryData, isEdit: true, page: currentPage },
    });
  };

  const handleClick = (rowData) => {
    navigate(`/itinerary-view-logs/${rowData.itinerarydetailsid}`);
  };

  const handlePreview = (rowData) => {
    window.open(`/view/trip/${rowData.itinerarydetailsid}/${slugify(rowData.itineraryname || "")}`, "_blank");
  };

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor("slNo", {
      header: "Sl. No",
      cell: (info) => info.row.index + 1,
    }),
    columnHelper.accessor("itineraryName", {
      header: "ITINERARY NAME",
      cell: (info) => {
        const itineraryName = info.getValue();
        const parts = itineraryName.split("Draft");
        return (
          <span className="flex items-center space-x-2">
            <span>{parts[0].trim()}</span>
            {parts.length > 1 && (
              <span className="bg-[#F9834E] text-white px-2 py-1 rounded text-sm">
                Draft
              </span>
            )}
          </span>
        );
      },
    }),
    columnHelper.accessor("package", { header: "PACKAGE" }),
    columnHelper.accessor("itinerary_travel", {
      header: "DATE OF TRAVEL",
      cell: (info) => new Date(info.getValue()).toISOString().split("T")[0],
    }),
    columnHelper.accessor("schoolname", { header: "INSTITUTIONS" }),
    columnHelper.accessor("actions", {
      header: "ACTIONS",
      cell: (info) => (
        <div className="flex gap-2 sm:gap-1 sm:w-[200px] sm:overflow-x-auto sm:pb-2 sm:pl-2">
          <button className="p-2 bg-gray-200 rounded-md">
            <span className="material-symbols-outlined" onClick={() => handleEditClick(info.row.original)}>
              edit
            </span>
          </button>
          {user?.role === "Admin" && (
            <>
              <button
                className="p-2 bg-gray-200 text-red-500 rounded-md"
                onClick={() => {
                  setSelectedPackage(info.row.original);
                  setShowDeleteModal(true);
                }}
              >
                <span className="material-symbols-outlined">delete</span>
              </button>
              <button
                className="p-2 bg-gray-200 rounded-md"
                onClick={() => generateItineraryPDF(info.row.original)}
              >
                <span className="material-symbols-outlined">picture_as_pdf</span>
              </button>
            </>
          )}
          <button
            className="p-2 bg-gray-200 rounded-md"
            onClick={() => handlePreview(info.row.original)}
            title="Preview itinerary"
          >
            <span className="material-symbols-outlined">visibility</span>
          </button>
          <button
            className="p-2 bg-gray-200 rounded-md"
            onClick={() => handleClick(info.row.original)}
          >
            <span className="material-symbols-outlined">visibility</span>
          </button>
          <button className="p-2 bg-gray-200 rounded-md" onClick={() => handleClick(info.row.original)}>
            <img src={log} alt="Itinerary Logo" className="lg:max-w-full lg:h-auto lg:w-4 w-6 max-w-28" />
          </button>
        </div>
      ),
    }),
  ];

  return (
    <div>
      <div className="flex justify-between">
        <div>
          <h2 className="text-primary-black text-[28px] font-semibold">Itinerary</h2>
          <p className="text-[14px]">
            <span className="font-bold">Itinerary</span> /{" "}
            <span className="font-regular">Itinerary List</span>
          </p>
        </div>
        <div className="flex mt-4 justify-end gap-4">
          {user?.role === "Admin" && (
            <Button type="primary" label="Add Itinerary" onClick={() => navigate("/add-itinerary")} />
          )}
        </div>
      </div>

      {/* Filter Fields */}
      <div className="grid grid-cols-1 gap-4 mt-4 md:grid-cols-2 md:gap-4 lg:flex lg:justify-between lg:items-center lg:gap-4">
        <div className="w-full lg:flex-[2]">
          <input
            type="text"
            className="outline-none p-2 rounded-l-lg w-full h-[40px]"
            placeholder="Search..."
            value={localSearch}
            onChange={handleSearch}
          />
        </div>

        <div className="w-full lg:flex-1">
          <SearchSelect
            className="w-full"
            value={packageOptions.find((o) => o.value === packageFilter) || null}
            options={packageOptions}
            onChange={(e) => handleFormSelection(e, "package")}
            placeholder="Select Package..."
          />
        </div>

        <div className="w-full lg:flex-1">
          <SearchSelect
            className="w-full"
            value={institutionOptions.find((o) => o.value === schoolFilter) || null}
            options={institutionOptions}
            onChange={(e) => handleFormSelection(e, "institution")}
            placeholder="Select Institution..."
          />
        </div>

        <div className="w-full lg:flex-1">
          <DatePicker
            selectedDate={selectedDate}
            onDateChange={(date) => setSelectedDate(date)}
            placeholderText="Select Date..."
            className="w-full mt-0"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border-gray-300 mt-7 pb-6">
        {isLoading ? (
          <LoaderComponent />
        ) : (
          <Table
            columns={columns}
            data={tableData}
            noOfPages={noOfPages}
            currentPage={currentPage}
            setCurrentPage={(page) => updateParams({ page: page > 1 ? String(page) : null })}
          />
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Delete Itinerary</h2>
            <p>
              Are you sure you want to delete itinerary{" "}
              <strong>{selectedPackage?.package}</strong>?
            </p>
            <div className="flex justify-end mt-6">
              <button
                className="px-4 py-2 bg-gray-200 rounded-md mr-2"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-500 text-white rounded-md"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default AllItinerary;
